/**
 * Territories — boundary mode (M8). Assign points to administrative boundaries
 * (ZIP / county / state); each boundary with members becomes a territory with a
 * rollup, persisting in `analysis.territories`.
 *
 * The ASSIGNMENT logic (point-in-boundary-polygon → territory + rollup) is pure
 * and testable NOW with fixture polygons. The real boundary geometry arrives at
 * DEPLOY TIME through the `BoundaryProvider` seam below — a vendor-abstracted
 * geo-core interface (same posture as the tile / routing abstractions), never a
 * hardcoded dataset. Attribution rides through non-removably.
 */

import type { Bounds, Point, PointSet } from "@/lib/map/pointset";
import { pointInRing, type Ring } from "./geometry";
import { rollupOf, territoryColor } from "./territories";
import type { BoundaryKind, Territory } from "./spec";

/** A single admin-boundary polygon (fixture in tests / dev, provider-sourced in prod). */
export interface BoundaryPolygon {
  kind: BoundaryKind;
  /** Stable boundary id (ZIP code, county FIPS, state code). */
  id: string;
  /** Display name (e.g. "King County", "94110"). */
  name: string;
  /** Outer ring, [lng,lat] vertices. */
  ring: Ring;
}

/**
 * The geo-core boundary-provider seam. A deployment binds a concrete
 * implementation (a self-hosted boundary dataset, a vendor API, …); this layer
 * only ever calls the interface, so a provider swap touches one adapter. Real
 * boundary data is a DEPLOY-TIME concern — no dataset is bundled here.
 */
export interface BoundaryProvider {
  /** Boundaries of `kind` intersecting `bbox` (async — data may be fetched). */
  getBoundaries(kind: BoundaryKind, bbox: Bounds): Promise<BoundaryPolygon[]>;
  /** Non-removable attribution for the boundary dataset (carried through like tiles). */
  readonly attribution: string;
}

/**
 * Assign points to boundaries: each point joins the FIRST boundary whose ring
 * contains it (boundaries are assumed non-overlapping admin units). Boundaries
 * with zero members are dropped. Pure — the caller supplies the polygons, so
 * this is the fixture-testable core of boundary mode.
 */
export function assignToBoundaries(
  pointSet: PointSet,
  boundaries: BoundaryPolygon[],
  opts?: { balanceBy?: string; palette?: string[] },
): Territory[] {
  const palette = opts?.palette ?? [];
  const byBoundary = new Map<string, Point[]>();
  for (const b of boundaries) byBoundary.set(b.id, []);

  for (const p of pointSet.points) {
    for (const b of boundaries) {
      if (pointInRing(p.lng, p.lat, b.ring)) {
        byBoundary.get(b.id)!.push(p);
        break;
      }
    }
  }

  const territories: Territory[] = [];
  let colorIndex = 0;
  for (const b of boundaries) {
    const members = byBoundary.get(b.id)!;
    if (members.length === 0) continue;
    territories.push({
      id: `terr_${b.kind}_${b.id}`,
      name: b.name,
      color: territoryColor(palette, colorIndex),
      polygon: b.ring,
      boundaryRef: { kind: b.kind, id: b.id },
      memberRowIds: members.map((p) => p.id),
      rollup: rollupOf(members, opts?.balanceBy),
    });
    colorIndex += 1;
  }
  return territories;
}

/**
 * End-to-end boundary assignment through the provider seam: fetch the
 * boundaries of `kind` covering the data's bbox, then run the pure assignment.
 * The provider is injected, so tests pass a fixture provider and prod passes the
 * geo-core one. Returns the territories plus the provider's attribution.
 */
export async function assignViaProvider(
  pointSet: PointSet,
  kind: BoundaryKind,
  provider: BoundaryProvider,
  opts?: { balanceBy?: string; palette?: string[] },
): Promise<{ territories: Territory[]; attribution: string }> {
  const bbox = pointSet.bounds;
  const boundaries = bbox ? await provider.getBoundaries(kind, bbox) : [];
  return { territories: assignToBoundaries(pointSet, boundaries, opts), attribution: provider.attribution };
}

/**
 * A fixture provider over in-memory polygons — the dev/test binding until a real
 * boundary dataset is wired at deploy time. Filters to the requested kind and
 * bbox-overlapping boundaries so it behaves like the real seam.
 */
export function createFixtureBoundaryProvider(
  boundaries: BoundaryPolygon[],
  attribution: string,
): BoundaryProvider {
  return {
    attribution,
    async getBoundaries(kind, bbox) {
      return boundaries.filter((b) => b.kind === kind && ringIntersectsBbox(b.ring, bbox));
    },
  };
}

function ringIntersectsBbox(ring: Ring, bbox: Bounds): boolean {
  for (const [lng, lat] of ring) {
    if (lng >= bbox.minLng && lng <= bbox.maxLng && lat >= bbox.minLat && lat <= bbox.maxLat) return true;
  }
  // Or the bbox corners fall inside the ring (ring larger than the data extent).
  const corners: [number, number][] = [
    [bbox.minLng, bbox.minLat],
    [bbox.maxLng, bbox.maxLat],
  ];
  return corners.some(([lng, lat]) => pointInRing(lng, lat, ring));
}
